import { clsx } from "clsx";
import type { ReactElement } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashboardWidgetBase } from "./DashboardWidgetBase.js";

interface ExopenProductsWidgetViewProps {
	/** One fully-built row per product (link/action wiring is the container's job). */
	items: ReactElement[];
}

const BlankProductItem = () => <div aria-hidden className="h-10" />;

const MIN_ROWS = 3;

export const ExopenProductsWidgetView = ({
	items,
}: ExopenProductsWidgetViewProps) => {
	const { t } = useTranslation();
	const containerRef = useRef<HTMLDivElement>(null);
	const rowRef = useRef<HTMLDivElement>(null);
	const [extraRows, setExtraRows] = useState(0);

	const baseRowCount = Math.max(MIN_ROWS, Math.ceil(items.length / 2));
	const rowCount = baseRowCount + extraRows;
	const cells: (ReactElement | null)[] = [
		...items,
		...Array.from(
			{ length: Math.max(0, rowCount * 2 - items.length) },
			() => null,
		),
	];
	const rows: (ReactElement | null)[][] = [];
	for (let index = 0; index < cells.length; index += 2) {
		rows.push(cells.slice(index, index + 2));
	}

	// Fill stretched dashboard height with same-size blank rows.
	useLayoutEffect(() => {
		const container = containerRef.current;
		const row = rowRef.current;
		if (!container || !row) return;
		const measure = () => {
			const rowHeight = row.offsetHeight;
			if (rowHeight === 0) return;
			const needed = Math.max(
				0,
				Math.floor(container.clientHeight / rowHeight) - baseRowCount,
			);
			setExtraRows((current) => (current === needed ? current : needed));
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		return () => observer.disconnect();
	}, [baseRowCount]);

	return (
		<DashboardWidgetBase title={t("Products")} flush>
			<div ref={containerRef} className="h-full">
				{rows.map((row, rowIndex) => {
					// A fully blank row's dividers would otherwise draw a grid
					// around nothing — fade them to a lighter grey instead.
					const isRowBlank = row[0] == null && row[1] == null;
					return (
						<div
							key={row[0]?.key ?? `blank-row-${rowIndex}`}
							ref={rowIndex === 0 ? rowRef : undefined}
							className={clsx(
								"grid grid-cols-1 border-b sm:grid-cols-2",
								isRowBlank ? "border-grey-50" : "border-grey-200",
							)}
						>
							<div
								className={clsx(
									"px-6 py-5",
									row.length > 1 &&
										(isRowBlank
											? "max-sm:border-b max-sm:border-grey-50"
											: "max-sm:border-b max-sm:border-grey-200"),
								)}
							>
								{row[0] ?? <BlankProductItem />}
							</div>
							<div
								className={clsx(
									"px-6 py-5 sm:border-l",
									isRowBlank ? "sm:border-grey-50" : "sm:border-grey-200",
								)}
							>
								{row[1] ?? <BlankProductItem />}
							</div>
						</div>
					);
				})}
			</div>
		</DashboardWidgetBase>
	);
};
